import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import OverlayToolbar, { DEVICES } from './OverlayToolbar';
import PreviewFrame from './PreviewFrame';
import useSegmentMutations from './useSegmentMutations';
import useReferenceData from './useReferenceData';
import SharePanel from './SharePanel';
import './preview.css';

const DEFAULT_OVERLAYS = {
    showSource: false,
    showStatus: true,
    showOverflow: true,
    showConfidence: false,
    showTerms: true,
    showPseudo: false,
};

const POLL_MS = 30000;

function blockMatches(block, filter, overrides, commentCounts) {
    switch (filter) {
        case 'untranslated': {
            const ov = overrides[block.sectionIndex] || {};
            const t = ov.target != null ? ov.target : block.target;
            return !t || !t.trim();
        }
        case 'lowconf':
            return block.confidence != null && block.confidence < 70;
        case 'comments':
            return (commentCounts[block.sectionIndex] || 0) > 0;
        default:
            return true;
    }
}

const Preview = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [preview, setPreview] = useState(null);
    const [activeLang, setActiveLang] = useState('');
    const [device, setDevice] = useState('desktop');
    const [overlays, setOverlays] = useState(DEFAULT_OVERLAYS);
    const [filter, setFilter] = useState('all');
    const [reviewMode, setReviewMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [lastSynced, setLastSynced] = useState(null);
    const [versions, setVersions] = useState([]);
    const [baselineIndex, setBaselineIndex] = useState(-1); // -1 = no comparison

    const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

    const handleAuthError = (err, fallbackMsg) => {
        if (err.response && err.response.status === 401) {
            dispatch(logout());
            navigate('/login');
        } else {
            setError(fallbackMsg);
        }
    };

    // Load the project list for the selector.
    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/projects?summary=true', authHeader)
            .then((r) => {
                const { createdProjects = [], sharedProjects = [] } = r.data || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                const list = [...unique.values()].filter((p) => !p.archivedAt);
                setProjects(list);
                if (list.length) setActiveProject(list[0]._id);
                setLoading(false);
            })
            .catch((err) => {
                handleAuthError(err, 'Failed to load projects.');
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Reset the language selection whenever the project changes.
    useEffect(() => {
        setActiveLang('');
    }, [activeProject]);

    // Load the structured preview for the active project + language.
    useEffect(() => {
        if (!activeProject || !token) return;
        setError(null);
        const url = `/api/projects/${activeProject}/preview${activeLang ? `?lang=${encodeURIComponent(activeLang)}` : ''}`;
        axios
            .get(url, authHeader)
            .then((r) => {
                setPreview(r.data);
                setLastSynced(new Date());
                if (!activeLang && r.data?.project?.targetLanguage) {
                    setActiveLang(r.data.project.targetLanguage);
                }
            })
            .catch((err) => handleAuthError(err, 'Failed to load preview.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProject, activeLang, token, reloadKey]);

    // Live sync: refresh on tab focus and on a light interval while visible, so
    // edits made in the Editor surface here without a manual reload. Open inline
    // edits are preserved — Block keys are stable, so refetching never remounts
    // a segment mid-edit.
    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === 'visible') setReloadKey((k) => k + 1);
        };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        const id = setInterval(refresh, POLL_MS);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
            clearInterval(id);
        };
    }, []);

    // Version history for the resolved language project (for the compare view).
    useEffect(() => {
        const pid = preview?.project?.id;
        if (!pid || !token) { setVersions([]); return; }
        axios
            .get(`/api/projects/${pid}/versions`, authHeader)
            .then((r) => setVersions(Array.isArray(r.data?.versions) ? r.data.versions : []))
            .catch(() => setVersions([]));
        setBaselineIndex(-1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview?.project?.id, token]);

    const mutations = useSegmentMutations({
        projectId: preview?.project?.id,
        token,
        initialTranslated: preview?.translatedSections,
    });

    const reference = useReferenceData({
        projectId: preview?.project?.id,
        glossaryId: preview?.project?.glossaryId,
        token,
    });

    const deviceWidth = DEVICES.find((d) => d.id === device).width;
    const proj = preview?.project;
    const groups = preview?.groups || [];
    const hasContent = groups.some((g) => g.blocks.length);

    const baseline = useMemo(() => {
        if (baselineIndex < 0 || !versions[baselineIndex]) return null;
        return versions[baselineIndex].translatedSections || [];
    }, [baselineIndex, versions]);

    const filteredGroups = useMemo(() => {
        if (filter === 'all') return groups;
        if (filter === 'overflow') {
            return groups.filter((g) => g.status === 'tight' || g.status === 'over');
        }
        return groups
            .map((g) => ({
                ...g,
                blocks: g.blocks.filter((b) => blockMatches(b, filter, mutations.overrides, reference.commentCounts)),
            }))
            .filter((g) => g.blocks.length);
    }, [groups, filter, mutations.overrides, reference.commentCounts]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading preview…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">In-context preview</h1>
                <div className="rs-page-actions" style={{ display: 'flex', gap: 8 }}>
                    <select
                        className="set-input"
                        value={activeProject || ''}
                        onChange={(e) => setActiveProject(e.target.value)}
                        style={{ height: 36 }}
                    >
                        {projects.map((p) => (
                            <option key={p._id} value={p._id}>{p.name || p.projectName || 'Untitled'}</option>
                        ))}
                    </select>
                    {preview?.languages?.length > 1 && (
                        <select
                            className="set-input"
                            value={activeLang}
                            onChange={(e) => setActiveLang(e.target.value)}
                            style={{ height: 36 }}
                        >
                            {preview.languages.map((l) => (
                                <option key={l.projectId} value={l.code}>
                                    {(l.code || '').toUpperCase()} · {l.progress}%
                                </option>
                            ))}
                        </select>
                    )}
                    {versions.length > 1 && (
                        <select
                            className="set-input"
                            value={baselineIndex}
                            onChange={(e) => setBaselineIndex(Number(e.target.value))}
                            style={{ height: 36 }}
                            title="Compare current translation against an earlier version"
                        >
                            <option value={-1}>Compare: off</option>
                            {versions.map((v, i) => (
                                i === versions.length - 1 ? null : (
                                    <option key={i} value={i}>
                                        vs v{i + 1}{v.updatedAt ? ` · ${new Date(v.updatedAt).toLocaleDateString()}` : ''}
                                    </option>
                                )
                            ))}
                        </select>
                    )}
                    {proj?.id && <SharePanel projectId={proj.id} token={token} />}
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <p className="rs-page-sub" style={{ margin: 0 }}>
                    See translations rendered the way your audience will read them.
                </p>
                <span style={{ flex: 1 }} />
                {lastSynced && (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                        Synced {lastSynced.toLocaleTimeString()}
                    </span>
                )}
                <button
                    type="button"
                    className="pv-btn sm"
                    onClick={() => setReloadKey((k) => k + 1)}
                >
                    Refresh
                </button>
            </div>

            <OverlayToolbar
                device={device}
                setDevice={setDevice}
                overlays={overlays}
                setOverlays={setOverlays}
                summary={preview?.summary}
                filter={filter}
                setFilter={setFilter}
                reviewMode={reviewMode}
                setReviewMode={setReviewMode}
            />

            <div style={{
                background: 'var(--bg-soft)', borderRadius: 16, padding: 20,
                display: 'flex', justifyContent: 'center', minHeight: 500,
            }}>
                <div style={{
                    width: `min(${deviceWidth}px, 100%)`,
                    background: 'var(--bg-card)',
                    borderRadius: 12, padding: '32px 40px',
                    boxShadow: 'var(--shadow-md)',
                    transition: 'width 0.2s',
                }}>
                    {proj && (
                        <>
                            <h2 style={{ marginTop: 0, fontSize: 20, letterSpacing: '-0.02em' }}>
                                {proj.name}
                            </h2>
                            <p style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: -4 }}>
                                Preview · {(proj.targetLanguage || '').toUpperCase()}
                                {preview.structured ? '' : ` · flat view${preview.flatReason ? ` (${preview.flatReason})` : ''}`}
                            </p>
                            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '12px 0 20px' }} />
                            {!hasContent && (
                                <p style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>
                                    This project has no segments yet.
                                </p>
                            )}
                            {hasContent && filteredGroups.length === 0 && (
                                <p style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>
                                    No segments match this filter.
                                </p>
                            )}
                            {mutations.saveError && (
                                <p style={{
                                    color: 'crimson', fontSize: 12.5, margin: '0 0 12px',
                                    background: 'rgba(220,20,60,0.06)', border: '1px solid var(--line)',
                                    borderRadius: 8, padding: '6px 10px',
                                }}>
                                    {mutations.saveError}
                                </p>
                            )}
                            {filteredGroups.map((group) => (
                                <PreviewFrame
                                    key={group.frameId}
                                    group={group}
                                    structured={preview.structured}
                                    sourceLang={proj.sourceLanguage}
                                    overlays={overlays}
                                    mutations={mutations}
                                    reference={reference}
                                    baseline={baseline}
                                    reviewMode={reviewMode}
                                />
                            ))}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Preview;
